import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowLeft, Bell, Check, Trash2, CheckCheck } from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';
import { Button } from '../components/Button';
import { Card } from '../components/Card';
import { WebLayout } from '../components/WebLayout';
import { useAuth } from '../contexts/AuthContext';
import { db, isFirebaseEnabled } from '../lib/firebase';
import { collection, query, where, orderBy, onSnapshot, updateDoc, doc, deleteDoc } from 'firebase/firestore';
interface Notification {
  id: string;
  type: 'quest' | 'collaboration' | 'subscription' | 'achievement' | 'focus' | 'system';
  title: string;
  message: string;
  read: boolean;
  createdAt: any;
  actionUrl?: string;
  icon?: string;
}
export function NotificationsPage() {
  const navigate = useNavigate();
  const {
    user
  } = useAuth();
  const [notifications, setNotifications] = useState<Notification[]>([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState<'all' | 'unread'>('all');
  useEffect(() => {
    if (!user) return;
    if (!isFirebaseEnabled || !db) {
      // Mock notifications for local mode
      setNotifications([{
        id: '1',
        type: 'quest',
        title: 'Quest Completed! 🎉',
        message: 'You completed "Finish project proposal"',
        read: false,
        createdAt: new Date(Date.now() - 3600000),
        icon: '🎯'
      }, {
        id: '2',
        type: 'collaboration',
        title: 'New Collaboration Invite',
        message: 'Alex invited you to collaborate on "Team Meeting Prep"',
        read: false,
        createdAt: new Date(Date.now() - 7200000),
        icon: '👥'
      }, {
        id: '3',
        type: 'achievement',
        title: 'Achievement Unlocked!',
        message: 'You earned "Quest Master" - Complete 10 quests',
        read: true,
        createdAt: new Date(Date.now() - 86400000),
        icon: '🏆'
      }]);
      setLoading(false);
      return;
    }
    // Real-time Firebase subscription
    const notificationsRef = collection(db, 'notifications');
    const q = query(notificationsRef, where('userId', '==', user.uid), orderBy('createdAt', 'desc'));
    const unsubscribe = onSnapshot(q, snapshot => {
      const notificationsData = snapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      })) as Notification[];
      setNotifications(notificationsData);
      setLoading(false);
    });
    return unsubscribe;
  }, [user]);
  const filteredNotifications = filter === 'unread' ? notifications.filter(n => !n.read) : notifications;
  const unreadCount = notifications.filter(n => !n.read).length;
  const handleMarkAsRead = async (notificationId: string) => {
    if (!isFirebaseEnabled || !db) {
      setNotifications(prev => prev.map(n => n.id === notificationId ? {
        ...n,
        read: true
      } : n));
      return;
    }
    try {
      await updateDoc(doc(db, 'notifications', notificationId), {
        read: true
      });
    } catch (error) {
      console.error('Error marking notification as read:', error);
    }
  };
  const handleMarkAllAsRead = async () => {
    const unreadNotifications = notifications.filter(n => !n.read);
    if (!isFirebaseEnabled || !db) {
      setNotifications(prev => prev.map(n => ({
        ...n,
        read: true
      })));
      return;
    }
    try {
      await Promise.all(unreadNotifications.map(n => updateDoc(doc(db, 'notifications', n.id), {
        read: true
      })));
    } catch (error) {
      console.error('Error marking all as read:', error);
    }
  };
  const handleDelete = async (notificationId: string) => {
    if (!isFirebaseEnabled || !db) {
      setNotifications(prev => prev.filter(n => n.id !== notificationId));
      return;
    }
    try {
      await deleteDoc(doc(db, 'notifications', notificationId));
    } catch (error) {
      console.error('Error deleting notification:', error);
    }
  };
  const getNotificationIcon = (type: string) => {
    const icons = {
      quest: '🎯',
      collaboration: '👥',
      subscription: '👑',
      achievement: '🏆',
      focus: '🧘',
      system: '🔔'
    };
    return icons[type as keyof typeof icons] || '🔔';
  };
  const formatTime = (timestamp: any) => {
    if (!timestamp) return '';
    const date = timestamp.toDate ? timestamp.toDate() : new Date(timestamp);
    const now = new Date();
    const diff = now.getTime() - date.getTime();
    const minutes = Math.floor(diff / 60000);
    const hours = Math.floor(diff / 3600000);
    const days = Math.floor(diff / 86400000);
    if (minutes < 1) return 'Just now';
    if (minutes < 60) return `${minutes}m ago`;
    if (hours < 24) return `${hours}h ago`;
    if (days < 7) return `${days}d ago`;
    return date.toLocaleDateString();
  };
  if (loading) {
    return <WebLayout data-id="element-1008">
        <div className="flex items-center justify-center min-h-screen" data-id="element-1009">
          <div className="text-center" data-id="element-1010">
            <div className="w-16 h-16 border-4 border-game-purple border-t-transparent rounded-full animate-spin mx-auto mb-4" data-id="element-1011"></div>
            <p className="font-quicksand text-gray-500 dark:text-gray-400" data-id="element-1012">
              Loading notifications...
            </p>
          </div>
        </div>
      </WebLayout>;
  }
  return <div className="min-h-screen bg-game-bg-pink dark:bg-[#1a1d29] pb-24" data-id="element-1013">
      <header className="px-6 pt-6 pb-4 sticky top-0 bg-game-bg-pink/90 dark:bg-[#1a1d29]/90 backdrop-blur-sm z-10" data-id="element-1014">
        <div className="flex items-center justify-between mb-4" data-id="element-1015">
          <div className="flex items-center gap-4" data-id="element-1016">
            <Button variant="ghost" size="sm" onClick={() => navigate(-1)} className="!p-2" data-id="element-1017">
              <ArrowLeft className="w-6 h-6" data-id="element-1018" />
            </Button>
            <div data-id="element-1019">
              <h1 className="text-2xl font-fredoka font-bold text-game-dark dark:text-white" data-id="element-1020">
                Notifications
              </h1>
              {unreadCount > 0 && <p className="text-sm text-gray-500 dark:text-gray-400 font-quicksand" data-id="element-1021">
                  {unreadCount} unread
                </p>}
            </div>
          </div>
          {unreadCount > 0 && <Button variant="secondary" size="sm" onClick={handleMarkAllAsRead} icon={<CheckCheck className="w-4 h-4" data-id="element-1023" />} data-id="element-1022">
              Mark all read
            </Button>}
        </div>

        {/* Filter Tabs */}
        <div className="flex gap-2 bg-white dark:bg-[#252936] p-1 rounded-2xl" data-id="element-1024">
          <button onClick={() => setFilter('all')} className={`flex-1 py-2 px-4 rounded-xl font-fredoka font-bold text-sm transition-all ${filter === 'all' ? 'bg-game-purple text-white' : 'text-gray-600 dark:text-gray-400'}`} data-id="element-1025">
            All
          </button>
          <button onClick={() => setFilter('unread')} className={`flex-1 py-2 px-4 rounded-xl font-fredoka font-bold text-sm transition-all ${filter === 'unread' ? 'bg-game-purple text-white' : 'text-gray-600 dark:text-gray-400'}`} data-id="element-1026">
            Unread {unreadCount > 0 && `(${unreadCount})`}
          </button>
        </div>
      </header>

      <main className="px-6 space-y-3 max-w-2xl mx-auto" data-id="element-1027">
        <AnimatePresence data-id="element-1028">
          {filteredNotifications.length === 0 ? <motion.div initial={{
          opacity: 0
        }} animate={{
          opacity: 1
        }} className="text-center py-16" data-id="element-1029">
              <div className="w-24 h-24 bg-gray-100 dark:bg-gray-800 rounded-full flex items-center justify-center mx-auto mb-4" data-id="element-1030">
                <Bell className="w-12 h-12 text-gray-400" data-id="element-1031" />
              </div>
              <h3 className="font-fredoka font-bold text-xl text-game-dark dark:text-white mb-2" data-id="element-1032">
                No notifications
              </h3>
              <p className="text-gray-500 dark:text-gray-400 font-quicksand" data-id="element-1033">
                {filter === 'unread' ? "You're all caught up!" : "You don't have any notifications yet"}
              </p>
            </motion.div> : filteredNotifications.map((notification, index) => <motion.div key={notification.id} initial={{
          opacity: 0,
          y: 20
        }} animate={{
          opacity: 1,
          y: 0
        }} exit={{
          opacity: 0,
          x: -100
        }} transition={{
          delay: index * 0.05
        }} data-id="element-1034">
                <Card className={`p-4 ${!notification.read ? 'bg-game-bg-purple dark:bg-purple-900/20 border-2 border-game-purple/30' : ''}`} data-id="element-1035">
                  <div className="flex gap-4" data-id="element-1036">
                    <div className="flex-shrink-0" data-id="element-1037">
                      <div className="w-12 h-12 bg-gray-100 dark:bg-gray-800 rounded-full flex items-center justify-center text-2xl" data-id="element-1038">
                        {notification.icon || getNotificationIcon(notification.type)}
                      </div>
                    </div>
                    <div className="flex-1 min-w-0" data-id="element-1039">
                      <div className="flex items-start justify-between gap-2 mb-1" data-id="element-1040">
                        <h3 className="font-fredoka font-bold text-game-dark dark:text-white" data-id="element-1041">
                          {notification.title}
                        </h3>
                        {!notification.read && <div className="w-2 h-2 bg-game-coral rounded-full flex-shrink-0 mt-2" data-id="element-1042"></div>}
                      </div>
                      <p className="text-sm text-gray-600 dark:text-gray-400 font-quicksand mb-2" data-id="element-1043">
                        {notification.message}
                      </p>
                      <div className="flex items-center gap-3" data-id="element-1044">
                        <span className="text-xs text-gray-500 dark:text-gray-500 font-quicksand" data-id="element-1045">
                          {formatTime(notification.createdAt)}
                        </span>
                        {!notification.read && <button onClick={() => handleMarkAsRead(notification.id)} className="text-xs text-game-purple font-fredoka font-bold hover:underline" data-id="element-1046">
                            Mark as read
                          </button>}
                        <button onClick={() => handleDelete(notification.id)} className="text-xs text-red-600 dark:text-red-400 font-fredoka font-bold hover:underline" data-id="element-1047">
                          Delete
                        </button>
                      </div>
                    </div>
                  </div>
                </Card>
              </motion.div>)}
        </AnimatePresence>
      </main>
    </div>;
}